import React, { useState } from 'react';
import { Phone, Bell, Users, UserPlus } from 'lucide-react';
import { PorterPicker } from './DriverPorters';
import { PorterAvatar, StatusPill } from '../porters/porterUi';
import { TimesheetEditor } from '../porters/TimesheetEditor';

export const JobCrew = ({ job, crewInfo, authFetch, onUpdated }) => {
  const [picking, setPicking] = useState(false);
  if (!crewInfo) return null;
  const { crew, porters_required: req, van } = crewInfo;
  const short = Math.max(0, req - crew.length);
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`job-crew-${job.ref}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Crew · {crew.length}/{req} porters{van ? ` · ${van.name || van.reg}` : ''}</div>
        {short > 0 && <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Needs {short} more</span>}
        {van?.off_road && <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-800">Van off road</span>}
      </div>
      {crew.length === 0 && <div className="text-xs text-slate-500 mt-1">No porters assigned yet.</div>}
      {!job.completed_at && (
        <button type="button" onClick={() => setPicking(true)} className="mt-2 h-10 px-3 rounded-md bg-[color:var(--mv-orange)] text-white text-sm font-bold inline-flex items-center gap-1.5" data-testid={`job-pick-porters-${job.ref}`}>
          <UserPlus className="w-4 h-4" /> Pick porters
        </button>
      )}
      {picking && <PorterPicker job={job} authFetch={authFetch} onClose={() => setPicking(false)} onChanged={onUpdated} />}
      {crew.map(c => (
        <div key={c.assignment.id} className="mt-2 pt-2 border-t border-slate-100 first:border-0 first:pt-0" data-testid={`job-crew-member-${job.ref}-${c.porter.id}`}>
          <div className="flex items-center gap-2">
            <PorterAvatar p={c.porter} size={30} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[color:var(--mv-navy)] truncate">{c.porter.name}</div>
              {c.porter.phone && <a href={`tel:${c.porter.phone}`} className="text-xs text-[color:var(--mv-orange)] inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.porter.phone}</a>}
            </div>
            <StatusPill status={c.status} />
          </div>
          <TimesheetEditor assignment={c.assignment} authFetch={authFetch} onSaved={onUpdated} />
        </div>
      ))}
    </div>
  );
};

export const MyPorters = ({ bookings, crewByJob }) => {
  const rows = bookings.flatMap(b => (crewByJob?.[b.id]?.crew || []).map(c => ({ b, c })));
  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="driver-my-porters">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Your crew</div>
      <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] flex items-center gap-2"><Users className="w-5 h-5 text-[color:var(--mv-orange)]" />My Porters</h2>
      {rows.length === 0 ? <div className="mt-2 text-xs text-slate-500" data-testid="my-porters-empty">No porters assigned to your upcoming jobs yet.</div> : (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          {rows.map(({ b, c }) => (
            <div key={c.assignment.id} className="rounded-lg border border-slate-200 p-3 flex items-center gap-3" data-testid={`my-porter-${c.porter.id}-${b.ref}`}>
              <PorterAvatar p={c.porter} size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[color:var(--mv-navy)] truncate">{c.porter.name}</div>
                {c.porter.phone && <a href={`tel:${c.porter.phone}`} className="text-xs text-[color:var(--mv-orange)]">{c.porter.phone}</a>}
                <div className="text-[11px] text-slate-500">#{b.ref} · {b.booking?.date} {b.booking?.time}</div>
              </div>
              <StatusPill status={c.status} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export const DriverNotifications = ({ data, authFetch, onRead }) => {
  const [open, setOpen] = useState(false);
  if (!data || data.notifications.length === 0) return null;
  const markRead = async () => { await authFetch('post', '/driver/notifications/read'); onRead(); };
  const list = open ? data.notifications : data.notifications.filter(n => !n.read).slice(0, 3);
  return (
    <section className={`mb-6 rounded-xl border p-4 ${data.unread ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`} data-testid="driver-notifications">
      <div className="flex items-center justify-between gap-2">
        <strong className="text-sm text-[color:var(--mv-navy)] inline-flex items-center gap-1.5"><Bell className="w-4 h-4" />Updates from the office{data.unread ? ` (${data.unread} new)` : ''}</strong>
        <div className="flex gap-2">
          <button type="button" onClick={() => setOpen(v => !v)} className="h-9 px-2 text-xs font-bold text-slate-600" data-testid="notifications-toggle">{open ? 'Hide' : 'Show all'}</button>
          {data.unread > 0 && <button type="button" onClick={markRead} className="h-9 px-3 rounded-md bg-[color:var(--mv-navy)] text-white text-xs font-bold" data-testid="notifications-mark-read">Mark read</button>}
        </div>
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {list.map(n => <li key={n.id} className={n.read ? 'text-slate-500' : 'text-[color:var(--mv-navy)] font-semibold'}>{n.message} <span className="text-[11px] text-slate-400">· {new Date(n.at + 'Z').toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span></li>)}
      </ul>
    </section>
  );
};
